
import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, KeyRound, CheckCircle, XCircle } from "lucide-react";
import { authApi } from "@/integrations/mysql/client";
import { Header } from "@/components/Header";

export default function ResetarSenha() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao redefinir senha.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-[#f4fbf7] flex items-center justify-center px-6 pt-20">
          <div className="max-w-md w-full text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-bold font-primary text-gray-800 text-xl mb-2">Link inválido</h2>
            <p className="text-gray-500 font-primary mb-6">Este link de redefinição é inválido ou expirou.</p>
            <button
              onClick={() => navigate("/logins")}
              className="text-brand-primary font-semibold font-primary hover:underline"
            >
              Voltar ao login
            </button>
          </div>
        </div>
      </>
    );
  }

  if (success) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-[#f4fbf7] flex items-center justify-center px-6 pt-20">
          <div className="max-w-md w-full text-center">
            <CheckCircle className="w-12 h-12 text-brand-primary mx-auto mb-4" />
            <h2 className="font-bold font-primary text-gray-800 text-xl mb-2">Senha redefinida!</h2>
            <p className="text-gray-500 font-primary mb-6">Sua senha foi alterada com sucesso.</p>
            <button
              onClick={() => navigate("/logins")}
              className="bg-brand-primary text-white font-bold font-primary rounded-full px-8 py-3 hover:bg-brand-primary/90 transition-colors"
            >
              Fazer login
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#f4fbf7] flex items-center justify-center px-6 pt-20">
        <div className="max-w-md w-full">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <KeyRound className="w-5 h-5 text-brand-primary" />
                Nova senha
              </CardTitle>
              <p className="text-gray-500 text-sm font-primary">
                Escolha uma nova senha para sua conta.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Nova senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mínimo 6 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirmar nova senha</Label>
                  <Input
                    id="confirm"
                    type={showPassword ? "text" : "password"}
                    placeholder="Repita a senha"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                </div>
                {error && (
                  <p className="text-red-500 text-xs font-primary">{error}</p>
                )}
                <Button
                  type="submit"
                  className="w-full bg-brand-primary hover:bg-brand-primary/90"
                  disabled={loading}
                >
                  {loading ? "Salvando..." : "Redefinir senha"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
